import { safeTimezone } from "./normalization.js";

export { normalizeTimezone, safeTimezone, sanitizeTimezoneForDB } from "./normalization.js";
export { isValidTimezone } from "./detection.js";

/**
 * Convert a Date object to a wall-clock Date in the given timezone.
 * The returned Date's local methods (.getHours(), .getDate(), …) reflect
 * the time at that location.
 */
export function convertToTimezone(date: Date, timeZone: string): Date {
  const tz = safeTimezone(timeZone);
  try {
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });

    const parts = formatter.formatToParts(date);
    const get = (type: Intl.DateTimeFormatPartTypes) =>
      parts.find((p) => p.type === type)?.value ?? "0";

    return new Date(
      parseInt(get("year"), 10),
      parseInt(get("month"), 10) - 1,
      parseInt(get("day"), 10),
      parseInt(get("hour"), 10),
      parseInt(get("minute"), 10),
      parseInt(get("second"), 10),
    );
  } catch (error) {
    console.error(`[timezone-engine] convertToTimezone failed for "${timeZone}", using UTC`, error);
    return date;
  }
}

/**
 * Format a Date using a simple token-based format string.
 *
 * Supported tokens: YYYY, YY, MM, DD, HH, mm, ss, SSS
 *
 * @example
 * formatDate(new Date(), "DD/MM/YYYY HH:mm")
 */
export function formatDate(date: Date, format: string): string {
  const pad = (n: number, len = 2) => String(n).padStart(len, "0");
  const tokens: Record<string, string> = {
    YYYY: String(date.getFullYear()),
    YY: String(date.getFullYear()).slice(-2),
    MM: pad(date.getMonth() + 1),
    DD: pad(date.getDate()),
    HH: pad(date.getHours()),
    mm: pad(date.getMinutes()),
    ss: pad(date.getSeconds()),
    SSS: pad(date.getMilliseconds(), 3),
  };

  let result = format;
  for (const [token, value] of Object.entries(tokens)) {
    result = result.replace(new RegExp(token, "g"), value);
  }
  return result;
}

/**
 * Convert a date to the site's timezone then format it.
 * Returns null if the date is invalid or not provided.
 */
export function formatDateForSite(
  date: Date | string | null | undefined,
  timeZone: string,
  dateTimeFormat: string,
): string | null {
  if (!date) return null;
  const dateObj = typeof date === "string" ? new Date(date) : date;
  if (isNaN(dateObj.getTime())) return null;
  const converted = convertToTimezone(dateObj, timeZone);
  return formatDate(converted, dateTimeFormat);
}

/**
 * Return the current timestamp formatted for a given site timezone + format.
 */
export function getFormattedDateTime(timeZone: string, dateTimeFormat: string): string {
  return formatDateForSite(new Date(), timeZone, dateTimeFormat) ?? new Date().toISOString();
}

/** Alias for token-based formatting — avoids clash with locale-aware formatDate */
export const formatDateWithPattern = formatDate;
